import * as React from "react";
import * as moment from "moment";
import {
    CommonHelpers,
    Document,
    IBaseRequestService,
    ICustomerPortalService,
    IncludedProductsEnum,
    Policy,
    Subscription,
    Types,
    ValuableItem,
    POLICY_STATUS_MTC,
    SESSION_STORAGE_KEY_CLAIM_POLICY
} from "@falcon/services";
import {ILookupService, Types as JourneyCoreTypeDefs} from "@athena-portal/plugin-journey";
import {ListItem} from "@athena-portal/journey-model";
import {lazyInject} from "@athena-portal/core";
import {ButtonLink} from "../../../../../components/navigation/ButtonLink";
import {mapDocumentTitles} from "../../../../../utils/DataMappers";
import {
    calculateValuablesTotalValue,
    getAddressLines,
    getAmountToDisplay,
    mergePolicyAndInteractionValuableItems,
    setSessionStorage
} from "../../../../../CommonFunctions";
import {IWithRouterProps, Overlay, withRouter} from "@athena-portal/components";
import {Link} from "react-router-dom";
import SpecifiedItemInfo from "./SpecifiedItemInfo";

interface IProps {
    policy: Policy;
    subscription: Subscription;
    policyIndex: number;
}

interface IState {
    documents?: Document[];
    documentPathToSizeMap: Map<string, string>;
}

export class YourRentersCoverPanelPlain extends React.Component<IProps & IWithRouterProps, IState> {

    private readonly cancelled: boolean = this.props.policy.status === POLICY_STATUS_MTC;
    private readonly disabledClass: string = this.cancelled ? "disabled" : "";

    private readonly flowRentersDOIPdfPath: string = "/resources/documents/LV_Flow_Renters/v2/LV_Flow_New_Renters_DOI_v2.pdf";
    private readonly flowRentersIPIDPdfPath: string = "/resources/documents/LV_Flow_Renters/v1/LV_Flow_Renters_IPID_v1.pdf";

    @lazyInject(Types.CustomerPortalService)
    private customerPortalService: ICustomerPortalService;

    @lazyInject(Types.BaseRequestService)
    private baseRequestService: IBaseRequestService;

    @lazyInject(JourneyCoreTypeDefs.LookupService)
    private lookupService: ILookupService;

    constructor(props: IProps & IWithRouterProps) {
        super(props);

        this.state = {
            documentPathToSizeMap: new Map<string, string>()
        };
    }

    public componentDidMount(): void {
        this.customerPortalService.getPolicyDocuments(this.props.policy.policyNumber).subscribe(
            (documents: Document[]) => {
                this.setState({
                    documents: mapDocumentTitles(documents, this.props.policy)
                });
            }
        );
        [this.flowRentersDOIPdfPath, this.flowRentersIPIDPdfPath].forEach((documentPath: string) => {
            this.baseRequestService.getDocumentSize(documentPath).subscribe(response => {
                    this.setState({
                        documentPathToSizeMap: this.state.documentPathToSizeMap.set(documentPath, this.formatFileSize(response.xhr.getResponseHeader('content-length')))
                    })
                }
            )
        });
    }

    public render() {
        return (
            <React.Fragment>
                <Overlay show={!this.state.documents}/>
                <div className="section-panel renters-covers-panel your-cover-panel">
                    <img className="main-icon" src="/img/Flow-car-cover.svg" alt="Renters cover icon"/>
                    {this.cancelled && this.renderCancellationDisclaimer()}
                    {this.renderCoverDetails(this.props.policyIndex)}
                    {this.renderSpecifiedItems()}
                    {this.renderPolicyHolderDetails()}
                    {this.renderPropertyDetails()}
                    {this.renderOtherResidents()}
                    {this.renderPolicyDocuments()}
                    <div className="section">
                        <div className="section-links">
                            {this.renderCancelAndReactivateButtons()}
                            <button type="button"
                                    className={`btn btn-primary btn-next plugin buttonLink`}
                                    onClick={this.redirectToClaim}>
                                Make a claim
                            </button>
                        </div>
                    </div>
                </div>
            </React.Fragment>
        );
    }

    private renderCancelAndReactivateButtons(): React.ReactNode {
        if (this.props.subscription.includedProducts === IncludedProductsEnum.MOTOR + IncludedProductsEnum.RENTERS) {
            return (
                <>
                    {this.cancelled ? (
                        this.renderReactivationButton()
                    ):(
                        <ButtonLink
                            id={`cancelRentersCover`}
                            to={`/journey/init/product/RentersInsurance/process/mtc/presetPage/${this.props.policy.interaction._id}`}
                        >Cancel renters cover</ButtonLink>
                    )}
                </>
            )
        } else {
            return <></>
        }
    }

    private renderReactivationButton(): React.ReactNode {
        if (this.props.subscription.allPoliciesAreCancelled || this.props.subscription.allRentersMTCsAreFromSupportPortal) {
            return <></>
        } else {
            return (
                <ButtonLink
                    id={`reactivateRentersCover`}
                    to={`/journey/show/product/RentersInsurance/process/mtaReinstateRentersCover/initializeInteraction/${this.props.policy.interaction._id}`}
                >Reactivate renters cover</ButtonLink>
            )
        }
    }

    private renderCancellationDisclaimer(): React.ReactNode {
        const endDate: moment.Moment = moment.utc(this.props.policy.endDate, moment.ISO_8601);

        let disclaimer: string;
        if (endDate.isSame(moment(), 'day')) {
            disclaimer = `Cover ends today`;
        } else if (endDate.isBefore(moment(), 'day')) {
            disclaimer = `Cover ended on ${endDate.format("D MMMM YYYY")}`;
        } else {
            disclaimer = `Cover will end on ${endDate.format("D MMMM YYYY")}`;
        }

        return (
            <div id={`rentersCancellationDisclaimer`} className="warning">
                {disclaimer}
            </div>
        );
    }

    private renderCoverDetails(policyIndex: number): React.ReactNode {
        const {policy, subscription} = this.props;
        const accidentalDamageWording: string = policy.interaction.factors.accidentalDamageProtection ? "Included" : "Not included";

        return (
            <div className="section">
                <div className="section-heading">Cover details</div>
                <div className="section-contents">
                    <div className="detail">
                        <div className="detail-key font-weight-extra-light">
                            Last subscription amount paid
                        </div>
                        <div className="detail-value price">{`${getAmountToDisplay(subscription.lastPaidAmountPerPolicy[policy.policyNumber as keyof Map<string, number>])}`}</div>
                    </div>
                    {!this.cancelled &&
                    <div className="detail">
                        <div className="detail-key font-weight-extra-light">Next payment date</div>
                        <div className="detail-value font-weight-semi-bold">{moment(subscription.nextPaymentDate, moment.ISO_8601).format("DD MMMM YYYY")}</div>
                        <div className="detail-value">(Payment will be calculated on {moment(subscription.renewalWindowStartDate, moment.ISO_8601).format("DD MMMM")})</div>
                    </div>
                    }
                    <div className="detail">
                        <div className="detail-key">Cover start</div>
                        <div className="detail-value" id={`renters${policyIndex}-startDate`}>{moment.utc(policy.startDate, moment.ISO_8601).format("DD/MM/YYYY")}</div>
                    </div>
                    <div className="detail">
                        <div className="detail-key">Cover end</div>
                        <div className="detail-value" id={`renters${policyIndex}-endDate`}>{this.cancelled ? moment.utc(policy.endDate, moment.ISO_8601).format("DD/MM/YYYY") : "Anytime"}</div>
                    </div>
                    <div className="detail">
                        <div className="detail-key">Contents cover limit</div>
                        <div className="detail-value" id={`renters${policyIndex}-contents-cover-limit`}>£{policy.interaction.factors.insuredValue}</div>
                    </div>
                    <div className="detail">
                        <div className="detail-key">Excess</div>
                        <div className="detail-value" id={`renters${policyIndex}-excess`}>£{policy.interaction.factors.excessAmount}</div>
                    </div>
                    <div className="detail">
                        <div className="detail-key">Accidental damage</div>
                        <div className="detail-value" id={`renters${policyIndex}-accidental-damage`}>{accidentalDamageWording}</div>
                    </div>
                    <div className="detail">
                        <div className={`detail-value link-edit text-decoration-underline ${this.disabledClass}`}>
                            <Link to={`/journey/init/product/RentersInsurance/process/mtaEditRentersCover/presetPage/${this.props.policy.interaction._id}`}>
                                Edit cover details
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    private renderSpecifiedItems = (): React.ReactNode => {
        const valuableItems: ValuableItem[] = mergePolicyAndInteractionValuableItems([this.props.policy]);
        if (valuableItems && valuableItems.length > 0) {
            const items: React.ReactNode[] = valuableItems.map((item: ValuableItem, index: number) => {
                return (
                    <div key={item.valuableItemId}>
                        {index !== 0 && <div className="dashed-10 my-4"/>}
                        <SpecifiedItemInfo {...this.props} item={item} interaction={this.props.policy.interaction} cancelled={this.cancelled}/>
                    </div>
                );
            });
            return (
                <div className="section text-left">
                    <div className="section-heading">Specified items</div>
                    <p>You have added <b>{valuableItems.length}</b> out of 10 items worth in total of <b>£{calculateValuablesTotalValue(valuableItems)}</b></p>
                    <div className="mt-2 p-3 bg-white-blue rounded">
                        {...items}
                    </div>
                </div>
            );
        } else {
            return <></>
        }
    };


    private renderPolicyHolderDetails(): React.ReactNode {
        const party = this.props.policy.policyHolder;
        const {policyHolder} = this.props.policy.interaction;
        const employmentStatus: string = policyHolder && policyHolder.employmentStatus ? this.fetchLookupValue("employmentStatus", policyHolder.employmentStatus) : "";
        const claims: string = policyHolder.claims && policyHolder.claims.length > 0 ? policyHolder.claims.length.toString() : "None";

        return (
            <div className="section">
                <div className="section-heading">Account holder details</div>
                <div className="section-contents">
                    <div className={"detail"}>
                        <div className="detail-value">
                            {party.firstName} {party.lastName}
                        </div>
                    </div>
                    <div className={"detail"}>
                        <div className="detail-value font-weight-semi-bold">Date of birth</div>
                        <div className="detail-value">
                            {moment(party.dateOfBirth, moment.ISO_8601).format("DD MMMM YYYY")}
                        </div>
                    </div>
                    <div className={"detail"}>
                        <div className="detail-key">Email</div>
                        <div className="detail-value">
                            {party.email}
                        </div>
                    </div>
                    <div className={"detail"}>
                        <div className="detail-key">Employment status</div>
                        <div className="detail-value">
                            <span>{employmentStatus}</span>
                            <div className={`detail-value link-edit text-decoration-underline ${this.disabledClass}`}>
                                <Link to="/journey/show/product/FlowSubscription/process/mtaEmploymentStatus/preset">
                                    Edit employment status
                                </Link>
                            </div>
                        </div>
                    </div>
                    <div className={"detail"}>
                        <div className="detail-key">Claims in the last 5 years</div>
                        <div className="detail-value">
                            <span>{claims}</span>
                            <div className={`detail-value link-edit text-decoration-underline ${this.disabledClass}`}>
                                <Link to={`/journey/init/product/RentersInsurance/process/mtaEditRentersClaims/presetPage/${this.props.policy.interaction._id}`}>
                                    Edit claims
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    private renderPropertyDetails(): React.ReactNode {
        const realEstate: any = this.props.policy.interaction.realEstates[0];
        const addressComponents: string[] = getAddressLines(realEstate.address);
        const addressComponentsToRender: React.ReactNode[] = addressComponents.map(
            (component: string, index: number) => (index % 2 === 0) && (index < addressComponents.length - 1) ?
                <span key={index} className="detail-value">{component}</span> :
                <React.Fragment><span key={index} className="detail-value">, {component}</span><br/></React.Fragment>
        );

        const furnishingType: string = realEstate && realEstate.furnishingType ? this.fetchLookupValue("furnishingType", realEstate.furnishingType) : "";
        const realEstateTypeSubType: string = realEstate && realEstate.realEstateTypeSubType ? this.fetchLookupValue("realEstateTypeSubType", realEstate.realEstateTypeSubType).toLowerCase() : "";

        const bedroomCnt: number = realEstate.bedroomCount;
        const bathroomCnt: number = realEstate.bathroomCount;
        const bedroomsAndBathroomsDescription: React.ReactNode = (
            <React.Fragment>
                with <span id="bedroom-cnt">{bedroomCnt}</span>{` bedroom${bedroomCnt === 1 ? "" : "s"} and `}
                <span id="bathroom-cnt">{bathroomCnt}</span>{` bathroom${bathroomCnt === 1 ? "" : "s"}`}
            </React.Fragment>
        );

        return (
            <div className="section">
                <div className="section-heading">Property details</div>
                <div className="section-contents">
                    <div className="detail">
                        <div className="detail-key">Insured address</div>
                        {addressComponentsToRender}
                    </div>
                    <div className={"detail"}>
                        <div className="detail-key">Your property</div>
                        <div className="detail-value">
                            <p className="m-0"><span id="furnishing-type">{furnishingType}</span> <span id="real-estate-type-subtype">{realEstateTypeSubType}</span></p>
                            <p className="m-0"><span id="bedrooms-bathrooms">{bedroomsAndBathroomsDescription}</span></p>
                        </div>
                        <div className={`detail-value link-edit text-decoration-underline ${this.disabledClass}`}>
                            <Link to="/journey/show/product/FlowSubscription/process/mtaChangeAddress/changeAddressPresetPage/">
                                Edit address
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    private renderOtherResidents(): React.ReactNode {
        const realEstate: any = this.props.policy.interaction.realEstates[0];
        let otherResidentsDescription: React.ReactNode;

        if (realEstate.hasNoOtherResidents) {
            otherResidentsDescription = <>None</>;
        } else {
            const familyMemberCnt: number = realEstate.adultFamilyMemberCount;
            const housemateCnt: number = realEstate.housemateCount;
            otherResidentsDescription = (
                <React.Fragment>
                    <span id="family-member-cnt">{familyMemberCnt}</span>{` family member${familyMemberCnt === 1 ? "" : "s"} and `}
                    <span id="housemate-cnt">{housemateCnt}</span>{` housemate${housemateCnt === 1 ? "" : "s"}`}
                </React.Fragment>
            );
        }

        return (
            <div className="section">
                <div className="section-heading">Other residents</div>
                <div className="section-contents">
                    <div className="detail">
                        <div className="detail-key">Adults over the age of 18</div>
                        <div className="detail-value">
                            {otherResidentsDescription}
                        </div>
                    </div>
                    <div className={"detail"}>
                        <div className="detail-key">Children under the age of 18</div>
                        <div id="other-residents-children" className="detail-value">
                            {CommonHelpers.booleanToText(realEstate.childrenAtProperty, "Yes", "No")}
                        </div>
                    </div>
                    <div className={`detail-value link-edit text-decoration-underline ${this.disabledClass}`}>
                        <Link to={`/journey/init/product/RentersInsurance/process/mtaOtherResidents/presetPage/${this.props.policy.interaction._id}`}>
                            Edit other residents
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    private renderPolicyDocuments(): React.ReactNode {
        return (
            <div className="section">
                <div className="section-heading">Policy documents</div>
                <div className="section-contents documents">
                    <ul className="list-unstyled document-list">
                        <li className="media">
                            <li className="document">
                                <a className="document-description no-underline text-left"
                                   href={this.flowRentersDOIPdfPath}
                                   download="Renters_DOI.pdf"
                                   id="document-renters-doi">
                                    <img className="policy-download-icon" alt="" src="/img/Flow-download.svg"/>
                                    <div className="document-description">Contents insurance policy document, {this.state.documentPathToSizeMap.get(this.flowRentersDOIPdfPath)}</div>
                                </a>
                            </li>
                        </li>
                        <li className="media">
                            <li className="document">
                                <a className="document-description no-underline pt-2 text-left"
                                   href={this.flowRentersIPIDPdfPath}
                                   download="Renters_IPID.pdf"
                                   id="document-renters-ipid">
                                    <img className="policy-download-icon" alt="" src="/img/Flow-download.svg"/>
                                    <div className="document-description">Contents insurance product information document, {this.state.documentPathToSizeMap.get(this.flowRentersIPIDPdfPath)}</div>
                                </a>
                            </li>
                        </li>
                    </ul>
                </div>
            </div>
        )
    }

    private formatFileSize = (byteSize: any) => {
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
        if (byteSize === 0) {
            return '';
        }
        const kilobyteSize: number = parseInt(String(Math.floor(Math.log(byteSize) / Math.log(1024))), 10);
        return Math.round(byteSize / Math.pow(1024, kilobyteSize)) + ' ' + sizes[kilobyteSize];
    };

    private fetchLookupValue(fieldId: string, id: string): string {
        let value: string = "";

        this.lookupService.fetchLookupValues(fieldId, "en-UK").subscribe((lookupValues: ListItem[]) => {
            value = lookupValues?.find(entry => entry.id === id)?.value ?? "";
        });

        return value;
    }

    private redirectToClaim = (event: any): void => {
        setSessionStorage(SESSION_STORAGE_KEY_CLAIM_POLICY, this.props.policy);
        this.props.navigate("/dashboard/rentersClaims/new",{state: {prevPath: location.pathname}});
    };
}

export const YourRentersCoverPanel = withRouter(YourRentersCoverPanelPlain)
